import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { usePopup } from 'context/PopupProvider';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import {
  ArrowLeft,
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Globe,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Server,
  User,
} from 'lucide-react';

const ENDPOINT_LABELS: Record<string, string> = {
  user_balance: 'User Balance',
  wallet_debit: 'Debit Request',
  battle_settle: 'Battle Settlement',
  table_leave: 'Leave Table',
  get_battle_settlement: 'Get Battle Settlement',
  get_user_battle_settlement: 'Get User Battle Settlement',
};

const DIRECTION_LABEL: Record<number, string> = {
  1: 'Inbound',
  2: 'Outbound',
};

interface Attempt {
  id: number;
  attemptNo: number;
  maxAttempts: number;
  retryDelayMs?: number;
  success: boolean;
  businessCode?: string;
  message?: string;
  errorType?: string;
  errorMessage?: string;
  fallbackAction?: string;
  operationSuccess?: boolean;
  durationMs?: number;
  requestBody?: unknown;
  responseBody?: unknown;
  requestHeaders?: unknown;
  responseStatus?: number;
  createdAt?: string;
  serviceName?: string;
  peerService?: string;
  httpMethod?: string;
  url?: string;
  path?: string;
  direction?: number;
  userId?: string;
  battleId?: string;
  transactionId?: string;
}

interface Summary {
  retryGroupId: string;
  endpoint: string;
  consumerName?: string;
  consumerCode?: string;
  consumerDbId?: number;
  battleId?: string;
  transactionId?: string;
  userId?: string;
  totalAttempts: number;
  maxAttempts?: number;
  operationSuccess: boolean;
  canRetry: boolean;
  firstAttemptAt?: string;
  lastAttemptAt?: string;
  httpMethod?: string;
  url?: string;
  path?: string;
  direction?: number;
  serviceName?: string;
  peerService?: string;
  lastBusinessCode?: string;
  lastMessage?: string;
  responseBalance?: number | null;
  lockedBalance?: number | null;
  currency?: string | null;
}

function fmtDate(v?: string) {
  return v ? new Date(v).toLocaleString() : '—';
}

function attemptTriggerMode(a: Pick<Attempt, 'fallbackAction'>): 'Manual' | 'Auto' {
  return a.fallbackAction === 'manual_retry' ? 'Manual' : 'Auto';
}

function formatFullApiCall(a: Pick<Attempt, 'httpMethod' | 'url' | 'path'>): string {
  const method = String(a.httpMethod || 'POST').toUpperCase();
  const target = String(a.url || a.path || '').trim();
  return target ? `${method} ${target}` : method;
}

function JsonPayloadBlock({
  label,
  data,
  variant,
  onCopy,
}: {
  label: string;
  data: unknown;
  variant: 'request' | 'success' | 'error';
  onCopy: () => void;
}) {
  const styles =
    variant === 'request'
      ? 'border-zinc-600 bg-zinc-900 text-zinc-100'
      : variant === 'success'
        ? 'border-emerald-600/60 bg-emerald-950 text-emerald-50'
        : 'border-red-600/60 bg-red-950 text-red-50';

  const labelStyles =
    variant === 'request'
      ? 'text-zinc-400'
      : variant === 'success'
        ? 'text-emerald-400'
        : 'text-red-400';

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className={`text-[10px] font-bold uppercase tracking-wider ${labelStyles}`}>{label}</p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCopy();
          }}
          className="inline-flex items-center gap-1 rounded-lg border border-line/50 bg-black/20 px-2 py-1 text-[10px] font-semibold text-inherit hover:bg-black/30"
        >
          <Copy size={12} /> Copy
        </button>
      </div>
      <pre
        className={`max-h-80 overflow-auto rounded-xl border p-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-all ${styles}`}
      >
        {JSON.stringify(data ?? {}, null, 2)}
      </pre>
    </div>
  );
}

function FullApiCallBar({
  apiCall,
  onCopy,
}: {
  apiCall: string;
  onCopy: () => void;
}) {
  return (
    <div className="mb-4 flex flex-col gap-2 rounded-xl border border-brand-gold/30 bg-brand-gold/5 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-brand-gold mb-1">Full API call</p>
        <p className="font-mono text-xs font-semibold text-heading break-all">{apiCall}</p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCopy();
        }}
        className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[10px] font-semibold hover:bg-subtle"
      >
        <Copy size={12} /> Copy URL
      </button>
    </div>
  );
}

function na(value?: string | null) {
  const t = String(value || '').trim();
  return t || null;
}

function DetailItem({
  label,
  value,
  mono,
  emptyHint,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  emptyHint?: string;
}) {
  const text = na(value);
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1">{label}</p>
      {text ? (
        <p className={`text-sm font-semibold text-heading break-all ${mono ? 'font-mono text-xs' : ''}`}>
          {text}
        </p>
      ) : (
        <p className="text-sm text-muted italic">{emptyHint || 'Not applicable'}</p>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: 'default' | 'success' | 'danger';
}) {
  const valueClass =
    tone === 'success'
      ? 'text-emerald-600'
      : tone === 'danger'
        ? 'text-red-600'
        : 'text-heading';
  return (
    <div className="rounded-xl border border-line bg-card p-4 shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted">{label}</p>
      <p className={`mt-1 text-xl font-extrabold tabular-nums ${valueClass}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted leading-snug">{sub}</p>}
    </div>
  );
}

async function copyJson(data: unknown) {
  try {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  } catch {
    /* ignore */
  }
}

const IntegrationLogDetail = () => {
  const { retryGroupId } = useParams<{ retryGroupId: string }>();
  const navigate = useNavigate();
  const { alert, confirm } = usePopup();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchDetail = useCallback(
    async (silent = false) => {
      if (!retryGroupId) return;
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const res = await api.get<{ summary: Summary; attempts: Attempt[] }>(
          API_ENDPOINTS.ADMIN.INTEGRATION_LOGS.GROUP_DETAIL(retryGroupId)
        );
        if (res.success && res.data) {
          setSummary(res.data.summary);
          setAttempts(res.data.attempts || []);
          if (res.data.attempts?.length) {
            setExpandedId((prev) => prev ?? res.data!.attempts[0].id);
          }
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [retryGroupId]
  );

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleManualRetry = async () => {
    if (!retryGroupId || !summary?.canRetry) return;
    const ok = await confirm({ message: 'Retry this API call with the same request parameters?' });
    if (!ok) return;
    setRetrying(true);
    try {
      const res = await api.post<{ success: boolean; message?: string; businessCode?: string }>(
        API_ENDPOINTS.ADMIN.INTEGRATION_LOGS.MANUAL_RETRY(retryGroupId),
        {}
      );
      if (res.success) {
        await alert({
          message: res.data?.success
            ? 'Manual retry succeeded'
            : `Manual retry failed: ${res.data?.message || res.message}`,
        });
        await fetchDetail(true);
      } else {
        await alert({ message: res.message || 'Retry failed' });
      }
    } finally {
      setRetrying(false);
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-muted">
        <RefreshCw className="h-8 w-8 animate-spin text-brand-gold" />
        <p className="text-sm font-medium">Loading log detail…</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="rounded-2xl border border-line bg-card p-10 text-center shadow-sm">
        <button
          type="button"
          onClick={() => navigate('/integration-logs')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Integration logs
        </button>
        <p className="text-muted font-medium">Log session not found.</p>
      </div>
    );
  }

  const endpointLabel = ENDPOINT_LABELS[summary.endpoint] || summary.endpoint;
  const consumerLabel =
    summary.consumerName || summary.consumerCode
      ? [summary.consumerName, summary.consumerCode].filter(Boolean).join(' · ')
      : null;
  const latestAttempt = attempts[0];
  const walletLine =
    summary.responseBalance != null
      ? `${summary.responseBalance.toLocaleString()}${summary.currency ? ` ${summary.currency}` : ''}${
          summary.lockedBalance != null ? ` (locked: ${summary.lockedBalance.toLocaleString()})` : ''
        }`
      : null;
  const summaryFullApi = formatFullApiCall(summary);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => navigate('/integration-logs')}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold hover:opacity-90"
          >
            <ArrowLeft className="h-4 w-4" /> Integration logs
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gold/15 text-brand-gold">
              <ScrollText size={22} />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-heading">{endpointLabel}</h1>
              <p className="mt-0.5 text-xs font-mono text-muted break-all">{summary.retryGroupId}</p>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                summary.operationSuccess
                  ? 'bg-emerald-500/15 text-emerald-700'
                  : 'bg-red-500/15 text-red-700'
              }`}
            >
              {summary.operationSuccess ? 'Success' : 'Failed'}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fetchDetail(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-line bg-card px-4 py-2.5 text-sm font-semibold shadow-sm hover:bg-subtle disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {summary.canRetry && (
            <button
              type="button"
              disabled={retrying}
              onClick={handleManualRetry}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-gold px-4 py-2.5 text-sm font-bold text-brand-dark hover:opacity-95 disabled:opacity-50"
            >
              <RotateCcw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
              Retry API
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Attempts"
          value={`${summary.totalAttempts} / ${summary.maxAttempts ?? summary.totalAttempts}`}
          sub={summary.totalAttempts > 1 ? 'Retry session' : 'Single attempt'}
        />
        <StatCard
          label="Outcome"
          value={summary.operationSuccess ? 'Success' : 'Failed'}
          tone={summary.operationSuccess ? 'success' : 'danger'}
          sub={summary.lastBusinessCode ? `Code ${summary.lastBusinessCode}` : undefined}
        />
        <StatCard
          label="Duration"
          value={latestAttempt?.durationMs != null ? `${latestAttempt.durationMs} ms` : '—'}
          sub="Latest attempt"
        />
        <StatCard
          label="Last attempt"
          value={summary.lastAttemptAt ? new Date(summary.lastAttemptAt).toLocaleTimeString() : '—'}
          sub={summary.lastAttemptAt ? new Date(summary.lastAttemptAt).toLocaleDateString() : undefined}
        />
      </div>

      {/* Context panels */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b border-line bg-subtle/50 px-5 py-3">
            <User size={16} className="text-brand-gold" />
            <h2 className="text-sm font-bold text-heading">Business context</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
            <DetailItem
              label="Consumer"
              value={consumerLabel}
              emptyHint="Consumer is resolved from platform DB using the launch secret-key header"
            />
            <DetailItem label="User ID" value={summary.userId} emptyHint="Not included in request" />
            <DetailItem
              label="Battle ID"
              value={summary.battleId}
              mono
              emptyHint="Not used for this API"
            />
            <DetailItem
              label="Transaction ID"
              value={summary.transactionId}
              mono
              emptyHint="Not used for this API"
            />
            {walletLine && (
              <div className="sm:col-span-2">
                <DetailItem label="Wallet (response)" value={walletLine} />
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b border-line bg-subtle/50 px-5 py-3">
            <Server size={16} className="text-brand-gold" />
            <h2 className="text-sm font-bold text-heading">HTTP context</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
            <DetailItem
              label="Direction"
              value={
                summary.direction != null
                  ? `${DIRECTION_LABEL[summary.direction] || summary.direction} · ${summary.serviceName || '—'} → ${summary.peerService || '—'}`
                  : null
              }
            />
            <DetailItem label="Method" value={summary.httpMethod} />
            <div className="sm:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1">Full API call</p>
              <p className="rounded-lg border border-line bg-zinc-900 px-3 py-2.5 font-mono text-xs font-semibold text-zinc-100 break-all">
                {summaryFullApi}
              </p>
            </div>
            <div className="sm:col-span-2">
              <DetailItem label="Path" value={summary.path} mono />
            </div>
            <div className="sm:col-span-2">
              <DetailItem label="Base URL" value={summary.url} mono />
            </div>
            <DetailItem label="First attempt" value={fmtDate(summary.firstAttemptAt)} />
            <DetailItem label="Last attempt" value={fmtDate(summary.lastAttemptAt)} />
          </div>
        </div>
      </div>

      {/* Last message */}
      {(summary.lastMessage || summary.lastBusinessCode) && (
        <div className="rounded-2xl border border-line bg-card px-5 py-4 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1">Latest response message</p>
          <p className="text-sm font-semibold text-heading">{summary.lastMessage || '—'}</p>
          {summary.lastBusinessCode && (
            <p className="mt-1 text-xs text-muted">Business code: {summary.lastBusinessCode}</p>
          )}
        </div>
      )}

      {/* Attempts table */}
      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={18} className="text-brand-gold" />
            <h2 className="font-bold text-heading">HTTP attempts</h2>
            <span className="rounded-full bg-subtle px-2 py-0.5 text-[10px] font-bold text-muted">
              {attempts.length}
            </span>
          </div>
          <p className="text-xs text-muted">Newest first · click a row to expand payload</p>
        </div>

        {attempts.length === 0 ? (
          <div className="px-5 py-14 text-center text-sm font-medium text-muted">Data not available</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className={`${LIST_TABLE_CLASS} min-w-[980px]`}>
                <thead className={LIST_THEAD_CLASS}>
                  <tr>
                    <th className={LIST_TH_NO_CLASS}>No.</th>
                    <th className={LIST_TH_CLASS}>Attempt</th>
                    <th className={LIST_TH_CLASS}>HTTP</th>
                    <th className={LIST_TH_CLASS}>Auto / Manual</th>
                    <th className={LIST_TH_CLASS}>Outcome</th>
                    <th className={LIST_TH_CLASS}>Code</th>
                    <th className={LIST_TH_CLASS}>Message</th>
                    <th className={LIST_TH_CLASS}>Duration</th>
                    <th className={LIST_TH_CLASS}>Time</th>
                    <th className={`${LIST_TH_CLASS} w-10`} />
                  </tr>
                </thead>
                <tbody className={LIST_TBODY_CLASS}>
                  {attempts.map((a, index) => {
                    const expanded = expandedId === a.id;
                    const rowMessage = a.message || a.errorMessage || '—';
                    const fullApiCall = formatFullApiCall(a);
                    const triggerMode = attemptTriggerMode(a);
                    return (
                      <React.Fragment key={a.id}>
                        <tr
                          className="border-t border-line cursor-pointer transition-colors hover:bg-subtle/50"
                          onClick={() => setExpandedId(expanded ? null : a.id)}
                        >
                          <td className={`${LIST_TD_NO_CLASS}`}>
                            {listRowNumber(1, index, attempts.length)}
                          </td>
                          <td className={LIST_TD_CLASS}>
                            <span className="font-semibold tabular-nums">
                              {a.attemptNo} / {a.maxAttempts}
                            </span>
                            {a.retryDelayMs != null && a.retryDelayMs > 0 && (
                              <p className="text-[10px] text-muted mt-0.5">delay {a.retryDelayMs}ms</p>
                            )}
                          </td>
                          <td className={LIST_TD_CLASS}>
                            <span className="font-mono text-xs font-semibold">{a.httpMethod || 'POST'}</span>
                            <p className="mt-1 font-mono text-[10px] text-muted break-all line-clamp-2" title={fullApiCall}>
                              {fullApiCall}
                            </p>
                            {a.responseStatus != null && (
                              <p className="text-[10px] text-muted mt-0.5">HTTP {a.responseStatus}</p>
                            )}
                          </td>
                          <td className={LIST_TD_CLASS}>
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                triggerMode === 'Manual'
                                  ? 'bg-amber-500/15 text-amber-800'
                                  : 'bg-sky-500/15 text-sky-800'
                              }`}
                            >
                              {triggerMode}
                            </span>
                          </td>
                          <td className={LIST_TD_CLASS}>
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                a.success
                                  ? 'bg-emerald-500/15 text-emerald-700'
                                  : 'bg-red-500/15 text-red-700'
                              }`}
                            >
                              {a.success ? 'OK' : 'Fail'}
                            </span>
                            {a.fallbackAction && a.fallbackAction !== 'manual_retry' && (
                              <p className="mt-1 text-[10px] font-semibold text-amber-700">{a.fallbackAction}</p>
                            )}
                          </td>
                          <td className={`${LIST_TD_CLASS} font-mono text-xs`}>{a.businessCode || '—'}</td>
                          <td className={`${LIST_TD_CLASS} max-w-[200px] truncate text-muted`}>{rowMessage}</td>
                          <td className={`${LIST_TD_CLASS} tabular-nums text-xs`}>
                            {a.durationMs != null ? `${a.durationMs} ms` : '—'}
                          </td>
                          <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>
                            {a.createdAt ? new Date(a.createdAt).toLocaleString() : '—'}
                          </td>
                          <td className={LIST_TD_CLASS}>
                            {expanded ? (
                              <ChevronDown className="h-4 w-4 text-muted" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted" />
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-t border-line bg-subtle/20">
                            <td colSpan={10} className="p-5">
                              <FullApiCallBar
                                apiCall={fullApiCall}
                                onCopy={() => void copyJson(fullApiCall)}
                              />
                              <div className="mb-4 flex flex-wrap gap-4 text-xs text-muted">
                                {a.serviceName && (
                                  <span className="inline-flex items-center gap-1">
                                    <Globe size={12} /> {a.serviceName} → {a.peerService}
                                  </span>
                                )}
                                {a.userId && (
                                  <span className="inline-flex items-center gap-1">
                                    <User size={12} /> {a.userId}
                                  </span>
                                )}
                                {a.createdAt && (
                                  <span className="inline-flex items-center gap-1">
                                    <Clock size={12} /> {new Date(a.createdAt).toLocaleString()}
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                <JsonPayloadBlock
                                  label="Request body"
                                  data={a.requestBody}
                                  variant="request"
                                  onCopy={() => void copyJson(a.requestBody)}
                                />
                                <JsonPayloadBlock
                                  label={`Response body · ${a.success ? 'Success' : 'Failed'}`}
                                  data={a.responseBody}
                                  variant={a.success ? 'success' : 'error'}
                                  onCopy={() => void copyJson(a.responseBody)}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IntegrationLogDetail;
